import { useSyncExternalStore } from 'react';

/**
 * What the console says out loud.
 *
 * Nothing on the board is announced today: a screen reader on this screen hears
 * silence while cards arrive, change lane and turn red. One polite live region
 * for the whole console, written through here, is what gives those events a
 * voice — and it is a single region on purpose, because several competing ones
 * interrupt each other and the operator hears fragments.
 *
 * It is a module store rather than a context so any part of the console can
 * speak without a provider having to be threaded through it.
 */

// The exact string the live region renders. Cached, because
// `useSyncExternalStore` compares snapshots by identity and a getter that builds
// a new string on every call re-renders forever.
let snapshot = '';

// Toggled on every announcement so repeating the same sentence still changes the
// region's content: a screen reader ignores a live region that did not change.
let alternate = false;

const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function announce(text: string): void {
  const trimmed = text.trim();

  if (trimmed === '') {
    return;
  }

  alternate = !alternate;
  // A zero-width space: invisible, and enough of a difference to re-announce.
  snapshot = alternate ? `${trimmed}\u200b` : trimmed;

  emit();
}

function getSnapshot(): string {
  return snapshot;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function useAnnouncement(): string {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
